// ** React Imports
import { useEffect, useState } from 'react'

// ** Third Party Components
import Select from 'react-select'
import { useForm, Controller } from 'react-hook-form'
import { ArrowLeft, ArrowRight } from 'react-feather'
import TimeZoneSelect from '../TimeZoneSelect.jsx'
import { useOnboarding } from '../OnboardingContext'
import ProfilePictureUploader from '../ProfilePictureUploader'
import { languageOptions, dateFormatOptions, numberFormatOptions } from '../Options'

// ** Utils
import { selectThemeColors } from '@utils'
import { useTranslation } from 'react-i18next'

// ** Reactstrap Imports
import { Label, Row, Col, Button, Form, Input, FormFeedback } from 'reactstrap'

// ** Styles
import '@styles/react/libs/react-select/_react-select.scss'

const CompanyInfo = ({ stepper }) => {
  // ** Hooks
  const {companyName, setCompanyName, companyShortName, setCompanyShortName, language, setLanguage, dateFormat, setDateFormat, numberFormat, setNumberFormat, companyLogo, setCompanyLogo, updateLatestStep} = useOnboarding()
  const companyInfo = {companyName, companyShortName, language, dateFormat, numberFormat}
  const [isSubmittable, setIsSubmittable] = useState(false)
  const { t } = useTranslation()

  const {
    control,
    setError,
    handleSubmit,
    formState: { errors }
  } = useForm({ companyInfo })

  const onSubmit = () => {
    if (Object.values(companyInfo).every(field => field.length > 0)) {
      updateLatestStep(3)
      stepper.next()
    } else {
      for (const key in companyInfo) {
        if (companyInfo[key].length === 0) {
          setError(key, {
            type: 'manual',
            message: `Please enter a valid ${key}`
          })
        }
      }
    }
  }

  const updateCompanyLogo = (logo) => {
    const reader = new FileReader()
    reader.onload = event => {
      const img = event.target.result
      setCompanyLogo(img)
    }
    reader.readAsDataURL(logo)
  }

  useEffect(() => {
    const canSubmit = Object.values(companyInfo).filter(v => typeof v !== "function" && v === "").length === 0
    setIsSubmittable(canSubmit)
  }, [companyInfo])

  return (
    <div>
      <div className='content-header'>
        <h5 className='mb-0'>{t('company-title')}</h5>
        <small>{t('company-subtitle')}</small>
      </div>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col md='2' className='mb-2'>
            <ProfilePictureUploader title={t('chose-company-logo')} pp={companyLogo} setPP={updateCompanyLogo} />
          </Col>
          <Col>
        <Row>
          <Col md='6' className='mb-1'>
            <Label className='form-label' for='companyName'>
              {t('company-name')}
            </Label>
            <Controller
              id='companyName'
              name='companyName'
              control={control}
              render={({ field }) => <Input placeholder={t('company-placeholder')} invalid={errors.companyName && true} {...field} value={companyInfo.companyName} onChange={e => { setCompanyName(e.target.value) }} />}
            />
            {errors.companyName && <FormFeedback>{errors.companyName.message}</FormFeedback>}
          </Col>
          <Col md='6' className='mb-1'>
            <Label className='form-label' for='companyShortName'>
              {t('company-short-name')}
            </Label>
            <Controller
              id='companyShortName'
              name='companyShortName'
              control={control}
              render={({ field }) => <Input placeholder={t('company-short-placeholder')} invalid={errors.companyShortName && true} {...field} value={companyInfo.companyShortName} onChange={e => { setCompanyShortName(e.target.value) }} />}
            />
            {errors.companyShortName && <FormFeedback>{errors.companyShortName.message}</FormFeedback>}
          </Col>
        </Row>
        <Row>
          {/* <Col md='4' className='mb-1'>
            <Label className='form-label' for='country'>
              {t('country')}
            </Label>
            <Select
              theme={selectThemeColors}
              isClearable={false}
              id={`country`}
              className='react-select'
              classNamePrefix='select'
              options={countryOptions}
              value={countryOptions.find(option => option.value === country)} 
              onChange={e => { setCountry(e.value) }}
            />
          </Col> */}
          <Col md='4' className='mb-1'>
            <Label className='form-label' for='language'>
              {t('language')}
            </Label>
            <Select
              isClearable={false}
              theme={selectThemeColors}
              id={`language`}
              options={languageOptions}
              className='react-select'
              classNamePrefix='select'
              value={languageOptions.find(option => option.value === language)}
              onChange={e => { setLanguage(e.value) }}
            />
          </Col>
          {/* <Col md='4' className='mb-1'>
              <Label className='form-label' for='timezone'>
                {t('timezone')}
              </Label>
              <TimeZoneSelect id='timezone' onChange={e => { setTimezone(e.value) }} />
            </Col>
          <Col md='4' className='mb-1'>
            <Label className='form-label' for='currency'>
            {t('currency')}
            </Label>
            <Select
              isClearable={false}
              theme={selectThemeColors}
              id={`currency`}
              options={currencyOptions}
              className='react-select'
              classNamePrefix='select'
              value={currencyOptions.find(option => option.value === currency)}
              onChange={e => { setCurrency(e.value) }}
            />
          </Col> */}
          <Col md='4' className='mb-1'>
            <Label className='form-label' for='dateFormat'>
              {t('date-format')}
            </Label>
            <Select
              theme={selectThemeColors}
              isClearable={false}
              id={`dateFormat`}
              className='react-select'
              classNamePrefix='select'
              options={dateFormatOptions}
              value={dateFormatOptions.find(option => option.value === dateFormat)}
              onChange={e => { setDateFormat(e.value) }}
            />
          </Col>
          <Col md='4' className='mb-1'>
            <Label className='form-label' for='numberFormat'>
              {t('number-format')}
            </Label>
            <Select
              isClearable={false}
              theme={selectThemeColors}
              id={`numberFormat`}
              options={numberFormatOptions}
              className='react-select'
              classNamePrefix='select'
              value={numberFormatOptions.find(option => option.value === numberFormat)}
              onChange={e => { setNumberFormat(e.value) }}
            />
          </Col>
        </Row>
        </Col>
        </Row>
        <div className='d-flex justify-content-between'>
          <Button color='primary' className='btn-prev' onClick={() => stepper.previous()}>
            <ArrowLeft size={14} className='align-middle me-sm-25 me-0'></ArrowLeft>
            <span className='align-middle d-sm-inline-block d-none'>{t('previous')}</span>
          </Button>
          <Button type='submit' color='primary' className='btn-next' disabled={!isSubmittable}>
            <span className='align-middle d-sm-inline-block d-none'>{t('next')}</span>
            <ArrowRight size={14} className='align-middle ms-sm-25 ms-0'></ArrowRight>
          </Button>
        </div>
      </Form>
    </div>
  )
}

export default CompanyInfo
